import { useState } from "react";
import imageSpade from "../../../assets/img/spade.png";
import imageClub from "../../../assets/img/club.png";
import imageHeart from "../../../assets/img/heart.png";
import imageDiamond from "../../../assets/img/diamond.png";
import Collapse from "../../../components/Collapse";
import { formatBook } from "../../../utils/formatOdds";

/**
 * 20-20 Teenpatti (teen20 / pteen20 / teen20b) player half — reference markup
 * (allpanel9 admin, desktop "isTablet" template):
 *   .teen20left|.teen20right >
 *     .casino-box-row > .casino-nation-name.no-border.casino-bl-box-title > .playera|.playerb
 *     .casino-bl-box.casino-bl-box-title  (Player / 3 Baccarat / Total / Pair Plus + range popups)
 *     .casino-bl-box.mb-4                 (odds; Pair Plus shows the player letter)
 *     .casino-rb-box-container.mb-3       (Black / Red boxes)
 * Styled by the ported reference rules (styles/index.css: .teenpatti20 ...).
 */

const LABELS = [
  { key: "player", label: "Player" },
  { key: "baccarat", label: "3 Baccarat" },
  { key: "total", label: "Total" },
  { key: "pairPlus", label: "Pair Plus" },
];

function formatStake(num) {
  if (num == null) return "0";
  const n = Number(num);
  if (n >= 100000) return `${n / 100000}L`;
  if (n >= 1000) return `${n / 1000}K`;
  return String(n);
}

// Reference: suspended = gstatus != "OPEN" (fallback: no odds)
const isSuspendedItem = (item) =>
  item?.gstatus != null ? String(item.gstatus).toUpperCase() !== "OPEN" : !(Number(item?.b) > 0);

// lockTopBook: a locked cell pins its padlock to the top only when it has a book
// (no book → padlock centred). teen20 / teen20b / teen20c pass it; VIP keeps lock-top always.
const BetTableTeenPatti20c = ({ data, onBetClick, playerName, exposures = {}, lockTopBook = false }) => {
  const [openRange, setOpenRange] = useState(null);
  // Reference range ids: #range1-#range6 for Player A, #range7-#range12 for Player B
  const rangeBase = playerName === "A" ? 0 : 6;

  const getScore = (nat) => data?.find((item) => item?.nat === nat) || { b: 0, min: 0, max: 0 };

  const toggleRange = (rangeId) => setOpenRange((prev) => (prev === rangeId ? null : rangeId));

  const score = {
    player: getScore(`Player ${playerName}`),
    baccarat: getScore(`3 Baccarat ${playerName}`),
    total: getScore(`Total ${playerName}`),
    pairPlus: getScore(`Pair Plus ${playerName}`),
    black: getScore(`Black ${playerName}`),
    red: getScore(`Red ${playerName}`),
  };

  const book = (exposureKey) => {
    const exposure = exposures[exposureKey] ?? 0;
    return {
      text: formatBook(exposure),
      has: exposure !== 0,
      cls: exposure > 0 ? "book-green" : "book-red",
    };
  };

  const rangeIcon = (rangeId, extraClass = "float-right") => (
    <i
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        toggleRange(rangeId);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          toggleRange(rangeId);
        }
      }}
      data-toggle="collapse"
      data-target={`#${rangeId}`}
      className={`fas fa-info-circle ${extraClass}`.trim()}
      aria-expanded={openRange === rangeId}
    />
  );

  const rangeBox = (rangeId, item) => (
    <Collapse id={rangeId} open={openRange === rangeId} className="icon-range">
      {"\n                                                R:"}
      <span>{formatStake(item?.min)}</span>-<span>{formatStake(item?.max)}</span>
    </Collapse>
  );

  const renderMainCell = (item, key, exposureKey, oddText) => {
    const value = item?.b ?? 0;
    const suspended = isSuspendedItem(item);
    const bk = book(exposureKey);
    return (
      <div
        key={key}
        className={`back casino-bl-box-item${suspended ? (!lockTopBook || bk.has ? " suspended lock-top" : " suspended") : ""}`}
        onClick={() => !suspended && onBetClick(value, exposureKey, item)}
      >
        <span className="casino-box-odd">{oddText ?? value}</span>{" "}
        <span className={`casino-book ${bk.cls}`}>{bk.text}</span>
      </div>
    );
  };

  const renderRbBox = (item, exposureKey, isBlack, rangeNo) => {
    const value = item?.b ?? 0;
    const suspended = isSuspendedItem(item);
    const bk = book(exposureKey);
    const rangeId = `range${rangeNo}`;
    return (
      <div className={`casino-rb-box ${isBlack ? "blackcontainer" : "redcontainer"}`}>
        <div
          className={`casino-rb-box-player ${isBlack ? "blackbox" : "redbox"} back${suspended ? " suspended" : ""}`}
          onClick={() => !suspended && onBetClick(value, exposureKey, item)}
        >
          {/* Reference quirk: only Player A's black box has the text-right icon wrapper */}
          <div className={isBlack && playerName === "A" ? "text-right" : undefined}>
            <img src={isBlack ? imageSpade : imageHeart} alt="" />{" "}
            <img src={isBlack ? imageClub : imageDiamond} alt="" className={isBlack ? undefined : "diamond-icon"} />
          </div>{" "}
          <div className="text-right">
            <span className="d-block casino-box-odd">{value}</span>{" "}
            <span className={`casino-book ${bk.cls}`}>{bk.text}</span>
          </div>
        </div>{" "}
        <div className="text-right casino-rb-box-player-range">
          {rangeIcon(rangeId)} {rangeBox(rangeId, item)}
        </div>
      </div>
    );
  };

  const playerClass = playerName === "A" ? "playera" : "playerb";

  return (
    <>
      <div className="casino-box-row">
        <div className="casino-nation-name no-border casino-bl-box-title">
          <div className={playerClass}>Player {playerName}</div>
        </div>
      </div>{" "}
      <div className="casino-bl-box casino-bl-box-title">
        {LABELS.map(({ key, label }, i) => {
          const rangeId = `range${rangeBase + i + 1}`;
          return (
            <div key={key} className="casino-bl-box-item">
              <span>{label}</span>{" "}
              <div className="ml-1">
                {rangeIcon(rangeId)} {rangeBox(rangeId, score[key])}
              </div>
            </div>
          );
        })}
      </div>{" "}
      <div className="casino-bl-box mb-4">
        {renderMainCell(score.player, "player", `Player ${playerName}`)}{" "}
        {renderMainCell(score.baccarat, "baccarat", `3 Baccarat ${playerName}`)}{" "}
        {renderMainCell(score.total, "total", `Total ${playerName}`)}{" "}
        {renderMainCell(score.pairPlus, "pairPlus", `Pair Plus ${playerName}`, playerName)}
      </div>{" "}
      <div className="casino-rb-box-container mb-3">
        {renderRbBox(score.black, `Black ${playerName}`, true, rangeBase + 5)}{" "}
        {renderRbBox(score.red, `Red ${playerName}`, false, rangeBase + 6)}
      </div>
    </>
  );
};

export default BetTableTeenPatti20c;
